import { PropsWithChildren, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ColumnDef } from '@tanstack/react-table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { DataTable } from '@/components/ui/data-table'
import { Badge } from '@/components/ui/badge'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { notificationTemplateVisibleForGameVersion } from 'app/frontend/base/helpers/notificationTemplateVisibility'

interface TemplateRow {
  category: string
  name: string
  description: string
}

const NotificationTemplatesDialog = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation()
  const {
    data: { gameVersion },
  } = usePageInfo()

  const rows = useMemo<TemplateRow[]>(() => {
    const types = t('notifications.types', { returnObjects: true }) as Record<
      string,
      { label: string; description: string }
    >

    return Object.entries(types)
      .filter(([key]) =>
        notificationTemplateVisibleForGameVersion(key, gameVersion),
      )
      .map(([key, type]) => {
        const firstSegment = key.split('-')[0]

        return {
          category:
            firstSegment.charAt(0).toUpperCase() + firstSegment.slice(1),
          name: type.label,
          description: type.description,
        }
      })
  }, [t, gameVersion])

  const columns: ColumnDef<TemplateRow>[] = useMemo(
    () => [
      {
        accessorKey: 'category',
        header: t('homepage.notification-templates.column-category'),
        size: 120,
        cell: (info) => (
          <Badge variant='outline'>{info.row.original.category}</Badge>
        ),
      },
      {
        accessorKey: 'name',
        header: t('homepage.notification-templates.column-name'),
        size: 200,
        cell: (info) => (
          <span className='font-medium'>{info.row.original.name}</span>
        ),
      },
      {
        accessorKey: 'description',
        header: t('homepage.notification-templates.column-description'),
        cell: (info) => <span>{info.row.original.description}</span>,
      },
    ],
    [t],
  )

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className='max-w-4xl'>
        <DialogHeader>
          <DialogTitle>
            {t('homepage.notification-templates.dialog-title')}
          </DialogTitle>
        </DialogHeader>

        <DataTable
          columns={columns}
          data={rows}
          pagination={8}
          cellClassName='align-top'
          includeHeader
        />
      </DialogContent>
    </Dialog>
  )
}

export default NotificationTemplatesDialog
